import { store } from '../../../app/store'
import type { PrescribingLocation } from '../../../features/locations/utils/prescribingLocation'
import { transformAddress } from '../../../utils/transformAddress'
import { get } from 'lodash'
import { format } from 'date-fns'
import { PrescribedItem, Prescription } from '../../../types'

export { transformAddress } from '../../../utils/transformAddress'

export type PrescriptionViewData = {
  prescriberTitle: string
  prescriberFirstname: string
  prescriberLastname: string
  prescriberNumber: string
  prescriberPhoneNumber: string
  prescriberOtherPhoneNumber: string
  prescriberFax: string
  prescriberProviderNumber: string
  prescriberHPII: string
  clinicName: string
  clinicAddress: string
  clinicAddress2: string
  clinicSuburb: string
  clinicPostcode: string
  clinicState: string
  clinicPhone: string
  patientFirstname: string
  patientLastname: string
  patientMedicareNumber: string
  patientMedicareIRN: string
  patientDvaNumber: string
  patientAddress: string
  patientSuburb: string
  patientState: string
  patientDob: string
  patientPostcode: string
  date: string
  concessionPensionNo: string
  pbsSafetyNetEntitlementCardHolder: boolean
  pbsConcessionCardHolder: boolean
  scriptId: string
  isPBS: boolean
  isRPBS: boolean
  ctgAnnotation: string
  brandSubstitutionNotAllowed: boolean
  erxBarcode: string
  prescribedItems: (PrescribedItem | undefined)[]
  sendViaEmail?: boolean
  sendViaSMS?: boolean
  printPaperToken?: boolean
  isPaperPrescription?: boolean
  recipientMobile?: string
  recipientEmail?: string
  prescriberEmail?: string
}

const str = (value: string | null | undefined) => value ?? ''

export const getPrescriptionViewDataForNewScript = (
  prescribingLocation?: PrescribingLocation | null
): PrescriptionViewData => {
  const state = store.getState()
  const patientAddressLine1 = get(state, 'patientDetail.selectedPatient.address_1', '')
  const patientAddressLine2 = get(state, 'patientDetail.selectedPatient.address_2', '')

  return {
    prescriberTitle: get(state, 'prescriber.prescriber.title', ''),
    prescriberFirstname: get(state, 'prescriber.prescriber.givenNames', ''),
    prescriberLastname: get(state, 'prescriber.prescriber.familyName', ''),
    prescriberNumber: get(state, 'prescriber.prescriber.prescriberNumber', ''),
    prescriberPhoneNumber:
      str(prescribingLocation?.phone_number) || get(state, 'prescriber.prescriber.phoneNumber', ''),
    prescriberOtherPhoneNumber: get(state, 'prescriber.prescriber.otherPhoneNumber', ''),
    prescriberFax: get(state, 'prescriber.prescriber.faxNumber', ''),
    prescriberProviderNumber:
      prescribingLocation?.providerNumber || get(state, 'prescriber.prescriber.providerNumber', ''),
    prescriberHPII: get(state, 'prescriber.prescriber.hpii', ''),
    clinicName: str(prescribingLocation?.name),
    clinicAddress: str(prescribingLocation?.address_1),
    clinicAddress2: str(prescribingLocation?.address_2),
    clinicSuburb: str(prescribingLocation?.suburb),
    clinicPostcode: str(prescribingLocation?.postcode),
    clinicState: str(prescribingLocation?.state),
    clinicPhone: str(prescribingLocation?.phone_number),
    prescriberEmail: prescribingLocation?.isWorkLocation
      ? str(prescribingLocation?.email) || get(state, 'prescriber.prescriber.email', '')
      : get(state, 'prescriber.prescriber.email', ''),
    patientFirstname: get(state, 'patientDetail.selectedPatient.given_names', ''),
    patientLastname: get(state, 'patientDetail.selectedPatient.family_name', ''),
    patientMedicareNumber: get(state, 'patientDetail.selectedPatient.medicare_no', ''),
    patientMedicareIRN: get(state, 'patientDetail.selectedPatient.medicare_irn', ''),
    patientDvaNumber: get(state, 'patientDetail.selectedPatient.dva_file_no', ''),
    patientAddress: transformAddress(patientAddressLine1, patientAddressLine2),
    patientSuburb: get(state, 'patientDetail.selectedPatient.suburb', ''),
    patientState: get(state, 'patientDetail.selectedPatient.state', ''),
    patientDob: get(state, 'patientDetail.selectedPatient.date_of_birth'),
    patientPostcode: get(state, 'patientDetail.selectedPatient.postcode', '').toString(),
    date: format(new Date(), 'dd/MM/yyyy'),
    concessionPensionNo: get(state, 'patientDetail.selectedPatient.concession_pension_no', ''),
    pbsSafetyNetEntitlementCardHolder: get(
      state,
      'patientDetail.selectedPatient.pbs_safety_net_entitlement_card_holder',
      false
    ), // Always false for now
    pbsConcessionCardHolder: get(
      state,
      'patientDetail.selectedPatient.pbs_concession_card_holder',
      false
    ), // Always false for now
    scriptId: get(state, 'newScript.prescribedItemList[0].prescriberScriptNumber', ''),
    isPBS: get(state, 'newScript.prescribedItemList[0].isPBS', false),
    isRPBS: get(state, 'newScript.prescribedItemList[0].isRPBS', false),
    ctgAnnotation: get(state, 'newScript.prescribedItemList[0].ctgAnnotation', ''),
    brandSubstitutionNotAllowed: get(
      state,
      'newScript.prescribedItemList[0].brandSubstitutionNotAllowed',
      false
    ),
    erxBarcode: '',
    prescribedItems: get(state, 'newScript.prescribedItemList', []),
    sendViaEmail: get(state, 'newScript.sendViaEmail', false),
    sendViaSMS: get(state, 'newScript.sendViaSMS', false),
    printPaperToken: get(state, 'newScript.printPaperToken', false),
    isPaperPrescription: get(state, 'newScript.isPaperPrescription', false),
    recipientMobile: get(state, 'newScript.recipientMobile', ''),
    recipientEmail: get(state, 'newScript.recipientEmail', ''),
  }
}

export const getPrescriptionViewData = (prescription: Prescription): PrescriptionViewData => {
  const state = store.getState()
  const currentClinic = state.clinic.currentClinic
  const embeddedLocation = prescription.issueSnapshot.location
  const usePrescriptionLocation = !!embeddedLocation?.address1
  const patient = prescription.issueSnapshot.patient
  const prescriber = prescription.issueSnapshot.prescriber

  const clinicAddressLine1 = usePrescriptionLocation
    ? get(embeddedLocation, 'address1', '')
    : get(currentClinic, 'address_1', '')
  const clinicAddressLine2 = usePrescriptionLocation
    ? get(embeddedLocation, 'address2', '')
    : get(currentClinic, 'address_2', '')
  const patientAddressLine1 = get(patient, 'address1', '')
  const patientAddressLine2 = get(patient, 'address2', '')
  const createdDate = new Date(get(prescription, 'createdDate', new Date()))
  return {
    prescriberTitle: get(prescriber, 'title', ''),
    prescriberFirstname: get(prescriber, 'givenNames', ''),
    prescriberLastname: get(prescriber, 'familyName', ''),
    prescriberNumber: get(prescriber, 'prescriberNumber', ''),
    prescriberPhoneNumber: get(prescriber, 'phoneNumber', ''),
    prescriberOtherPhoneNumber: get(prescriber, 'otherPhoneNumber', ''),
    prescriberFax: get(prescriber, 'faxNumber', ''),
    prescriberProviderNumber: get(prescriber, 'providerNumber', ''),
    prescriberHPII: get(prescriber, 'hpii', ''),
    clinicName: usePrescriptionLocation
      ? get(embeddedLocation, 'name', '')
      : get(currentClinic, 'name', ''),
    clinicAddress: str(clinicAddressLine1),
    clinicAddress2: str(clinicAddressLine2),
    clinicSuburb: usePrescriptionLocation
      ? get(embeddedLocation, 'suburb', '') ?? ''
      : get(currentClinic, 'suburb', ''),
    clinicPostcode: usePrescriptionLocation
      ? get(embeddedLocation, 'postcode', '') ?? ''
      : get(currentClinic, 'postcode', ''),
    clinicState: usePrescriptionLocation
      ? get(embeddedLocation, 'state', '') ?? ''
      : get(currentClinic, 'state', ''),
    clinicPhone: str(
      usePrescriptionLocation
        ? get(embeddedLocation, 'phoneNumber', '')
        : get(currentClinic, 'phone_number', '')
    ),
    patientFirstname: get(patient, 'givenNames', ''),
    patientLastname: get(patient, 'familyName', ''),
    patientMedicareNumber: get(patient, 'medicareNo', ''),
    patientMedicareIRN: get(patient, 'medicareIrn', ''),
    patientDvaNumber: get(patient, 'dvaFileNo', ''),
    patientAddress: transformAddress(patientAddressLine1, patientAddressLine2),
    patientSuburb: get(patient, 'suburb', ''),
    patientState: get(patient, 'state', ''),
    patientDob: get(patient, 'dateOfBirth'),
    patientPostcode: get(patient, 'postcode', '').toString(),
    date: format(createdDate, 'dd/MM/yyyy'),
    concessionPensionNo: get(patient, 'concessionPensionNo', ''),
    pbsSafetyNetEntitlementCardHolder: get(
      patient,
      'pbs_safety_net_entitlement_card_holder',
      false
    ), // TBC: we don't have this field
    pbsConcessionCardHolder: get(patient, 'pbsConcessionCardHolder', false), // TBC: we don't have this field
    scriptId: get(prescription, 'prescribedItems[0].prescriberScriptNumber'),
    isPBS: get(prescription, 'prescribedItems[0].isPBS', false),
    isRPBS: get(prescription, 'prescribedItems[0].isRPBS', false),
    ctgAnnotation: get(prescription, 'prescribedItems[0].ctgAnnotation', ''),
    brandSubstitutionNotAllowed: get(
      prescription,
      'prescribedItems[0].brandSubstitutionNotAllowed',
      false
    ),
    erxBarcode: '',
    prescribedItems: get(prescription, 'prescribedItems', []),
  }
}
